import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import type { CustomerStatus, IngestionStatus, PaymentStatus, QuoteStatus } from '../lib/types';
import { IconAlert, IconX } from './icons';

// ------------------------------------------------------------------ buttons

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'accent' | 'danger' | 'danger-solid' | 'ghost';
  size?: 'sm' | 'md';
  icon?: boolean;
  loading?: boolean;
};

export function Button({ variant = 'default', size = 'md', icon, loading, className = '', children, disabled, type = 'button', ...rest }: BtnProps) {
  const cls = [
    'btn',
    variant === 'danger-solid' ? 'danger solid' : variant !== 'default' ? variant : '',
    size === 'sm' ? 'sm' : '',
    icon ? 'icon' : '',
    className,
  ].join(' ');
  return (
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="spinner" /> : null}
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ form fields

export function Field({
  label,
  required,
  hint,
  error,
  children,
  className = '',
  htmlFor,
}: {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <div className={`field ${className}`}>
      <label htmlFor={htmlFor}>
        {label}
        {required ? <span className="req" aria-hidden>*</span> : null}
      </label>
      {children}
      {error ? <span className="field-error" role="alert">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

export const Input = ({ className = '', ltr, ...p }: ComponentProps<'input'> & { ltr?: boolean }) => (
  <input className={`input ${ltr ? 'ltr-input' : ''} ${className}`} {...p} />
);
export const Select = ({ className = '', ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select className={`select ${className}`} {...p} />;
export const Textarea = ({ className = '', ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className={`textarea ${className}`} {...p} />;

export function Checkbox({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; disabled?: boolean }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

/** Numbers, emails, IDs and phones inside Hebrew text. */
export const Ltr = ({ children, className = '' }: { children: ReactNode; className?: string }) => <bdi className={`ltr ${className}`}>{children}</bdi>;

// ------------------------------------------------------------------ badges

const STATUS_TONE: Record<CustomerStatus, string> = { active: 'ok', inactive: '', prospect: 'info' };
export const CustomerStatusBadge = ({ status }: { status: CustomerStatus }) => (
  <span className={`badge ${STATUS_TONE[status]}`}>{t.enums.customerStatus[status]}</span>
);

const PAY_TONE: Record<PaymentStatus, string> = { paid: 'ok', partial: 'info', open: '', overdue: 'bad', cancelled: 'cancelled', not_applicable: '' };
export const PaymentBadge = ({ status }: { status: PaymentStatus }) =>
  status === 'not_applicable' ? <span className="faint">—</span> : <span className={`badge ${PAY_TONE[status]}`}>{t.enums.paymentStatus[status]}</span>;

const QUOTE_TONE: Record<QuoteStatus, string> = { draft: '', sent: 'info', approved: 'ok', rejected: 'bad', expired: 'warn' };
export const QuoteBadge = ({ status }: { status: QuoteStatus | null }) =>
  status ? <span className={`badge ${QUOTE_TONE[status]}`}>{t.enums.quoteStatus[status]}</span> : null;

const ING_TONE: Record<IngestionStatus, string> = {
  processing: 'info',
  matched: 'ok',
  processed: 'accent',
  needs_review: 'warn',
  duplicate: '',
  ignored: 'outline',
  failed: 'bad',
};
export const IngestionBadge = ({ status }: { status: IngestionStatus }) => (
  <span className={`badge ${ING_TONE[status]}`}>{t.enums.ingestionStatus[status]}</span>
);

export function Confidence({ value }: { value: number | null }) {
  if (value == null) return <span className="faint">—</span>;
  const pct = Math.round(value * 100);
  const tone = value >= 0.9 ? '' : value >= 0.6 ? 'mid' : 'low';
  return (
    <span className={`conf ${tone}`} title={`רמת ביטחון ${pct}%`}>
      <span className="conf-bar"><span style={{ width: `${pct}%` }} /></span>
      <bdi>{pct}%</bdi>
    </span>
  );
}

// ------------------------------------------------------------------ states

export function Empty({ title, body, actions }: { title: string; body?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {body ? <p>{body}</p> : null}
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : t.common.loadError;
  return (
    <div className="empty">
      <h3>{t.common.loadError}</h3>
      <p>{msg}</p>
      {onRetry ? (
        <div className="actions">
          <Button onClick={onRetry}>{t.common.retry}</Button>
        </div>
      ) : null}
    </div>
  );
}

export function SkeletonRows({ rows = 6, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <span className="skeleton" style={{ width: `${40 + ((r * 7 + c * 13) % 45)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function Callout({ tone = 'info', title, children, action }: { tone?: 'info' | 'warn' | 'bad' | 'ok'; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={`callout ${tone}`} role={tone === 'bad' ? 'alert' : undefined}>
      {tone === 'warn' || tone === 'bad' ? <IconAlert style={{ marginTop: 3, flex: 'none' }} /> : null}
      <div style={{ flex: 1 }}>
        {title ? <div className="callout-title">{title}</div> : null}
        {children}
      </div>
      {action}
    </div>
  );
}

// ------------------------------------------------------------------ table helpers

export function SortTh<K extends string>({
  label,
  sortKey,
  sort,
  dir,
  onSort,
  className = '',
}: {
  label: ReactNode;
  sortKey: K;
  sort: K;
  dir: 'asc' | 'desc';
  onSort: (k: K) => void;
  className?: string;
}) {
  const active = sort === sortKey;
  return (
    <th
      className={`sortable ${className}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort(sortKey)}
    >
      {label}
      <span className="sort-ind">{active ? (dir === 'asc' ? '↑' : '↓') : ''}</span>
    </th>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="table-foot">
      <span className="tabular">
        {from}–{to} {t.common.of} {total}
      </span>
      <span className="spacer" />
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>{t.common.prev}</Button>
      <span className="tabular">
        {t.common.page} {page} {t.common.of} {pages}
      </span>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>{t.common.next}</Button>
    </div>
  );
}

// ------------------------------------------------------------------ dialogs

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'lg' | 'xl';
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose();
      if (e.key === 'Tab' && ref.current) {
        // Keep keyboard focus inside the dialog.
        const f = ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const list = [...f].filter((el) => !el.hasAttribute('disabled'));
        if (!list.length) return;
        const first = list[0]!;
        const last = list[list.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const focusTarget = ref.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button.primary, button');
    focusTarget?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [open, onClose, dismissable]);
  if (!open) return null;
  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && dismissable && onClose()}>
      <div className={`dialog ${size ?? ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <span className="spacer" />
          {dismissable ? (
            <Button variant="ghost" size="sm" icon onClick={onClose} aria-label={t.common.close}>
              <IconX />
            </Button>
          ) : null}
        </div>
        <div className="dialog-body">{children}</div>
        {footer ? <div className="dialog-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

type ConfirmOpts = { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean };
const ConfirmCtx = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog
        open={Boolean(state)}
        onClose={() => close(false)}
        title={state?.title}
        footer={
          <>
            <Button variant={state?.danger ? 'danger-solid' : 'primary'} onClick={() => close(true)} autoFocus>
              {state?.confirmLabel ?? t.common.confirm}
            </Button>
            <Button onClick={() => close(false)}>{t.common.cancel}</Button>
          </>
        }
      >
        {state?.body ?? null}
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

// ------------------------------------------------------------------ toasts

type Toast = { id: number; message: ReactNode; tone: 'ok' | 'bad' };
const ToastCtx = createContext<{ success: (m: ReactNode) => void; error: (e: unknown) => void }>({ success: () => {}, error: () => {} });
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: ReactNode, tone: Toast['tone']) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts.slice(-3), { id, message, tone }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), tone === 'bad' ? 7000 : 3500);
  }, []);
  const api = {
    success: useCallback((m: ReactNode) => push(m, 'ok'), [push]),
    error: useCallback((e: unknown) => push(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t.common.unknownError, 'bad'), [push]),
  };
  return (
    <ToastCtx.Provider value={api}>
      {children}
      {createPortal(
        <div className="toasts" role="status" aria-live="polite">
          {toasts.map((x) => (
            <div key={x.id} className={`toast ${x.tone === 'bad' ? 'bad' : ''}`}>
              <span>{x.message}</span>
              <button className="toast-close" onClick={() => setToasts((ts) => ts.filter((y) => y.id !== x.id))} aria-label={t.common.close}>
                <IconX size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

// ------------------------------------------------------------------ misc

export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: Array<{ key: K; label: ReactNode; count?: number }>; value: K; onChange: (k: K) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tb) => (
        <button key={tb.key} role="tab" aria-selected={value === tb.key} className={`tab ${value === tb.key ? 'active' : ''}`} onClick={() => onChange(tb.key)}>
          {tb.label}
          {tb.count != null ? <span className="count">{tb.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Browser prompt before closing/reloading the tab while a form has unsaved changes. */
export function useUnsavedWarning(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}

export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
